/**
 * Affirmation — end-of-round card.
 *
 * A heavy paper card centered in the page. A single themed motif "pressed"
 * into the upper edge like a wax seal. The motif IS the celebration mark —
 * no particle burst, no silhouette.
 */
import { motion, useReducedMotion } from 'motion/react';

import { cardSpring } from './motion';
import type { ThemeSurface } from './themes';

import './Affirmation.css';

export interface AffirmationProps {
  surface: ThemeSurface;
  durationMs: number;
  wordsFound: number;
  wordsTotal: number;
  mode: 'daily' | 'practice';
  /** Re-roll THIS category — "Another one". */
  onAnother: () => void;
  /** Switch to the NEXT category. */
  onNext: () => void;
  /** End the session (back to the menu / host). */
  onBack: () => void;
}

export function Affirmation({
  surface,
  durationMs,
  wordsFound,
  wordsTotal,
  mode,
  onAnother,
  onNext,
  onBack,
}: AffirmationProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const min = Math.floor(durationMs / 60_000);
  const sec = Math.floor((durationMs % 60_000) / 1000);
  const timeLabel = min === 0 ? `${sec}s` : `${min}:${sec.toString().padStart(2, '0')}`;

  // Plain, warm headlines.
  const headlines = ['Nicely done.', 'All found.', 'Lovely.', 'Beautifully done.'];
  const headline = headlines[durationMs % headlines.length] ?? headlines[0]!;

  return (
    <motion.section
      className="ts-aff"
      initial={reduced ? { opacity: 1 } : { opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={cardSpring}
      aria-live="polite"
    >
      <div className="ts-aff__card">
        <p className="ts-aff__completion">
          {mode === 'daily' ? 'Daily' : 'Practice'} {surface.title} puzzle complete.
        </p>
        <h2 className="ts-aff__headline">{headline}</h2>
        <p className="ts-aff__subhead">
          You found every word in the {surface.title.toLowerCase()} puzzle.
        </p>

        <dl className="ts-aff__stats">
          <div className="ts-aff__stat">
            <dt>Time</dt>
            <dd>{timeLabel}</dd>
          </div>
          <div className="ts-aff__stat">
            <dt>Words</dt>
            <dd>
              {wordsFound}
              <span className="ts-aff__stat-of"> of </span>
              {wordsTotal}
            </dd>
          </div>
          <div className="ts-aff__stat">
            <dt>Theme</dt>
            <dd>{surface.title}</dd>
          </div>
          <div className="ts-aff__stat">
            <dt>Mode</dt>
            <dd>{mode === 'daily' ? 'Daily' : 'Practice'}</dd>
          </div>
        </dl>

        <div className="ts-aff__actions">
          <button type="button" className="ts-aff__btn ts-aff__btn--secondary" onClick={onBack}>
            Back to menu
          </button>
          <button type="button" className="ts-aff__btn ts-aff__btn--secondary" onClick={onNext}>
            Try another theme
          </button>
          <button
            type="button"
            className="ts-aff__btn ts-aff__btn--primary"
            onClick={onAnother}
            autoFocus
          >
            {mode === 'daily' ? 'Practice this theme' : 'Another puzzle'}
          </button>
        </div>
      </div>
    </motion.section>
  );
}
